import { useState } from "react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import {
	Divider,
	Collapse,
	List,
	ListItemButton,
	ListItemText,
	Typography,
	Avatar,
	TextField,
	Switch,
	ListItemIcon,
	Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// cmp
import RowEntry from "../../../row-entry";
import EditableSelectList from "../editable-select";
import Svg from "../../../svg";
// services
import { icons } from "@local/theme";
// types
import type { ChangeEvent } from "react";

interface Info {
	bat_type?: string;
	rocid?: string;
	category?: string;
	type?: string;
	endpoint?: string;
	quickview_category?: string;
	is_group?: boolean;
	is_presence?: boolean;
	plugin?: string;
	color?: string;
}

type Props = Readonly<{
	selectedItem?: Info;
	previousItem: Info;
	fieldUpdateCount: number;
	onChange: (key: string, value: string) => void;
	fieldUpdate: (event: ChangeEvent<HTMLInputElement>) => void;
	onToggleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}>;

interface Option {
	id: string;
	label: string;
	primary: string;
	secondary: string;
}

const createOptionsFromL10ns = (l10ns: Record<string, string>): Array<Option> => (
	Object.keys(l10ns).map((l10n) => ({
		id: l10n,
		label: l10n,
		primary: l10ns[l10n] ?? l10n,
		secondary: l10n,
	}))
);

const OPTIONS = {
	DC: createOptionsFromL10ns(i18next.t("deviceCategories", {returnObjects: true})),
	DT: createOptionsFromL10ns(i18next.t("rocIds", { returnObjects: true })),
	QC: createOptionsFromL10ns(i18next.t("quickCategories", { returnObjects: true })),
} as const;

const DEFAULT_SELECTED_ITEM = {
	bat_type: "",
	rocid: "",
	category: "",
	type: "",
	endpoint: "",
	quickview_category: "",
	is_group: false,
	is_presence: false,
	plugin: "",
	color: "",
} as const satisfies Info;

const InfoSection = ({ selectedItem = DEFAULT_SELECTED_ITEM, previousItem, fieldUpdateCount, onChange, fieldUpdate, onToggleChange }: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(true);

	return (
		<List disablePadding={true}>
			<ListItemButton
				onClick={() => (setExpanded(!expanded))}
				style={{ paddingTop: "12px", paddingBottom: "12px" }}
			>
				<ListItemText
					primary={
						<Typography variant="body2" style={{ fontWeight: "bold" }}>
							{t("customTableEditor.sections.info")}
						</Typography>
					}
				/>
				<Avatar className="count-badge">{fieldUpdateCount}</Avatar>
				<ListItemIcon>
					{expanded
						? <icons.ExpandLess htmlColor={theme.vars.palette.text.secondary} />
						: <Svg src="navigation/arrowDropDown.svg" color={theme.vars.palette.text.secondary} />
					}
				</ListItemIcon>
			</ListItemButton>
			<Divider />
			<Collapse in={expanded} timeout="auto">
				<RowEntry title={t("customTableEditor.rocid.text")} subTitle={t("customTableEditor.rocid.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.rocid !== selectedItem.rocid &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.rocid ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<TextField
						fullWidth={true}
						name="rocid"
						placeholder="0000"
						disabled={true}
						value={selectedItem.rocid ?? ""}
						onChange={fieldUpdate}
						style={{ width: "calc(100% - 56px)" }}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.bat_type.text")} subTitle={t("customTableEditor.bat_type.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.bat_type !== selectedItem.bat_type &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.bat_type ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<TextField
						fullWidth={true}
						name="bat_type"
						value={selectedItem.bat_type ?? ""}
						onChange={fieldUpdate}
						style={{ width: "calc(100% - 56px)" }}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.category.text")} subTitle={t("customTableEditor.category.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.category !== selectedItem.category &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.category ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<EditableSelectList
						name="category"
						placeholder="dc_*"
						value={selectedItem.category ?? ""}
						options={OPTIONS.DC}
						onChange={onChange}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.type.text")} subTitle={t("customTableEditor.type.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.type !== selectedItem.type &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.type ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<EditableSelectList
						name="type"
						placeholder="dt_*"
						value={selectedItem.type ?? ""}
						options={OPTIONS.DT}
						onChange={onChange}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.endpoint.text")} subTitle={t("customTableEditor.endpoint.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.endpoint !== selectedItem.endpoint &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.endpoint ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<TextField
						fullWidth={true}
						name="endpoint"
						placeholder="00"
						value={selectedItem.endpoint ?? ""}
						onChange={fieldUpdate}
						style={{ width: "calc(100% - 56px)" }}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.quickview_category.text")} subTitle={t("customTableEditor.quickview_category.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.quickview_category !== selectedItem.quickview_category &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.quickview_category ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<EditableSelectList
						name="quickview_category"
						placeholder="qc_*"
						value={selectedItem.quickview_category ?? ""}
						options={OPTIONS.QC}
						onChange={onChange}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.is_group.text")} subTitle={t("customTableEditor.is_group.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.is_group !== selectedItem.is_group &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.is_group === undefined ? "NA" : previousItem.is_group.toString()}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<div style={{ width: "calc(100% - 56px)" }}>
						<Switch
							name="is_group"
							checked={selectedItem.is_group ?? false}
							onChange={onToggleChange}
						/>
					</div>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.is_presence.text")} subTitle={t("customTableEditor.is_presence.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.is_presence !== selectedItem.is_presence &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.is_presence === undefined ? "NA" : previousItem.is_presence.toString()}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<div style={{ width: "calc(100% - 56px)" }}>
						<Switch
							name="is_presence"
							checked={selectedItem.is_presence ?? false}
							onChange={onToggleChange}
						/>
					</div>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.plugin.text")} subTitle={t("customTableEditor.plugin.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.plugin !== selectedItem.plugin &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.plugin ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<TextField
						fullWidth={true}
						name="plugin"
						value={selectedItem.plugin ?? ""}
						onChange={fieldUpdate}
						style={{ width: "calc(100% - 56px)" }}
					/>
				</RowEntry>
				<Divider />
				<RowEntry title={t("customTableEditor.color.text")} subTitle={t("customTableEditor.color.id")} styleRight={{ margin: "-8px 0", display: "flex", justifyContent: "flex-end" }}>
					{previousItem.color !== selectedItem.color &&
						<ListItemIcon style={{ alignItems: "center" }}>
							<Tooltip title={previousItem.color ?? "NA"}>
								<Svg src="actions/editLine.svg" color={theme.vars.palette.primary.main} />
							</Tooltip>
						</ListItemIcon>
					}
					<TextField
						fullWidth={true}
						name="color"
						placeholder="#RRGGBBAA"
						value={selectedItem.color ?? ""}
						onChange={fieldUpdate}
						style={{ width: "calc(100% - 56px)" }}
					/>
				</RowEntry>
				<Divider />
			</Collapse>
		</List>
	);
};

export default InfoSection;
